import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Modal } from "../../components/UI/Modal";
import { deleteProjects, getAllProjects } from "../../api/Projects";
import { Project } from "../../utils/interface/project";
import Listing from "../../components/Projects/Listing";
import Pagination from "../../components/UI/Pagination";
import { AlertTriangle } from "lucide-react";
import UserProfileCard from "../../components/UserProfileCard";
import { useAuth } from "../../contexts/AuthContext";
import type { Pagination as PaginationType } from "../../utils/interface/pagination";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import SearchInput from "../../components/UI/SearchInput";
import { queryKeys } from "../../utils/queryKeys";

const ProjectManagement = () => {
  const navigate = useNavigate();
  const { userDetails } = useAuth();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedProject, setSelectedProjects] = useState<any | null>(null);
  // Pagination and search state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debounceTimer, setDebounceTimer] = useState<
    ReturnType<typeof setTimeout> | null
  >(null);
  const queryClient = useQueryClient();

  const {
    data,
    isLoading: loading,
  } = useQuery<{ projects?: Project[]; pagination?: PaginationType }>({
    queryKey: queryKeys.projects.list({
      page: currentPage,
      limit: 6,
      search: searchQuery,
    }),
    queryFn: async () => getAllProjects(currentPage, 6, searchQuery.trim()),
  });
  const projectList = data?.projects ?? [];
  const pagination = data?.pagination ?? null;

  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: string) => deleteProjects(projectId),
    onSuccess: async () => {
      setIsDeleteModalOpen(false);
      setSelectedProjects(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects.all() });
    },
  });

  const handleView = (project: Project) => {
    navigate({
      to: "/project-management/view/$id",
      params: { id: project.id },
      state: { project },
    });
  };
  const handleEditProject = (project: Project) => {
    navigate({
      to: "/project-management/edit/$id",
      params: { id: project.id },
      state: { project },
    });
  };
  const handleDelete = (projects: Project) => {
    setSelectedProjects(projects);
    setIsDeleteModalOpen(true);
  };
  const handleDeleteProject = async (id: string) => {
    await deleteProjectMutation.mutateAsync(id);
  };

  // Handle search with debounce (when user types, reset to page 1)
  useEffect(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      setCurrentPage(1);
    }, 500);

    setDebounceTimer(timer);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounceTimer intentionally omitted to avoid re-running on timer update
  }, [searchQuery]);

  return (
    <div className="">
      <UserProfileCard />
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 ">
        <div>
          <h1 className="text-[35px] sm:[40px] lg:text-[49px] 2xl:text-[59px] font-display font-light text-white mb-2">
            Projects
          </h1>
          <p className="text-white  text-[14px] lg:text-[16px] xl:text-[18px] font-light">
            {userDetails?.type === "2"
              ? "Here is the list of projects that you have access to upload files for"
              : "Here is the list of projects currently on the system"}
          </p>
        </div>

        {userDetails?.type === "0" && (
          <Link
            to="/project-management/add"
            className="text-base ml-auto font-semibold group flex items-center min-w-[178px] gap-3 px-6 py-[7px] rounded-[27px] border-2 border-[#E761A0] bg-[#E761A0] text-white transition-all duration-300 ease-out hover:bg-transparent hover:text-[#E761A0] active:scale-95 add-btn"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="31.852"
              height="31.852"
              viewBox="0 0 31.852 31.852"
              className="w-[36px] h-[36px] fill-white transition-all duration-300 ease-out group-hover:fill-[#E761A0]"
            >
              <g
                id="Group_87"
                data-name="Group 87"
                transform="translate(-1570.776 -286.775)"
              >
                <path
                  id="Path_232"
                  data-name="Path 232"
                  d="M33.633,4.462V1.408A1.411,1.411,0,0,1,35.041,0h7.111A2.545,2.545,0,0,1,43.91.671l1.216,1.085a2.543,2.543,0,0,0,1.758.67H60.868a1.41,1.41,0,0,1,1.408,1.408V6.413a.352.352,0,0,1-.352.352H49.244a1.594,1.594,0,0,1-.478-.066,1.578,1.578,0,0,1-.428-.218l-1.435-1a3.572,3.572,0,0,0-2.108-.659L33.984,4.814a.352.352,0,0,1-.351-.352"
                  transform="translate(1538.816 286.775)"
                  fillRule="evenodd"
                />
                <path
                  id="Path_233"
                  data-name="Path 233"
                  d="M1.409,125.178a1.363,1.363,0,0,0-1.035.454A1.363,1.363,0,0,0,.005,126.7l1.354,16.547a1.4,1.4,0,0,0,1.4,1.293H6.023a1,1,0,0,0,1-.984,8.908,8.908,0,0,1,17.815,0,1,1,0,0,0,1,.984h3.425a1.4,1.4,0,0,0,1.4-1.293l1.194-14.59a1.408,1.408,0,0,0-1.4-1.522H17.285a3.613,3.613,0,0,1-2.111-.663l-1.435-1a1.493,1.493,0,0,0-.9-.288l-10-.008H1.409Z"
                  transform="translate(1570.775 167.827)"
                  fillRule="evenodd"
                />
                <path
                  id="Path_234"
                  data-name="Path 234"
                  d="M188.8,355.338a7.136,7.136,0,1,1-10.091,0,7.136,7.136,0,0,1,10.091,0m-.384,4.29v1.511a.346.346,0,0,1-.345.345h-3.215V364.7a.346.346,0,0,1-.346.345H183a.346.346,0,0,1-.345-.345v-3.215h-3.215a.346.346,0,0,1-.345-.345v-1.511a.346.346,0,0,1,.345-.345h3.215v-3.215a.346.346,0,0,1,.345-.345h1.511a.346.346,0,0,1,.346.345v3.215h3.215a.346.346,0,0,1,.345.345"
                  transform="translate(1402.946 -48.892)"
                  fillRule="evenodd"
                />
              </g>
            </svg>
            <span>ADD PROJECT</span>
          </Link>
        )}
      </div>

      <div className="pt-[54px]">
        <div className="">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="SEARCH"
            className="max-w-md"
          />
        </div>

        {/* Search and Filter Section */}
        <div className="">

          <div className="">
            <Listing
              projectList={projectList}
              handleEdit={handleEditProject}
              handleDelete={handleDelete}
              handleView={handleView}
              isDeleteModal={isDeleteModalOpen}
              loading={loading}
            />

            {pagination && pagination.totalPages > 1 && (() => {
              const totalCount = pagination.totalItems ?? pagination.currentlyShowing;
              const showingFrom = Math.min((currentPage - 1) * pagination.limit + 1, totalCount);
              const showingTo = Math.min(currentPage * pagination.limit, totalCount);
              return (
                <Pagination
                  currentPage={currentPage}
                  totalPages={pagination.totalPages}
                  onPageChange={setCurrentPage}
                  totalItems={totalCount}
                  itemsPerPage={pagination.limit}
                  showingFrom={showingFrom}
                  showingTo={showingTo}
                />
              );
            })()}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!deleteProjectMutation.isPending) {
            setIsDeleteModalOpen(false);
            setSelectedProjects(null);
          }
        }}
        className="max-w-md bg-slate-800/95 backdrop-blur-xl border border-white/10 shadow-2xl"
      >
        <div className="p-8">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-600/20 border border-red-600/30 mb-4 mx-auto">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white text-center mb-2">
            Delete Project
          </h2>
          <p className="text-gray-400 text-center mb-6">
            Are you sure you want to delete{" "}
            <span className="font-semibold text-white">
              {selectedProject?.name}
            </span>
            ? This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setIsDeleteModalOpen(false);
                setSelectedProjects(null);
              }}
              disabled={deleteProjectMutation.isPending}
              className="flex-1 px-4 py-3 border border-white/20 text-gray-300 rounded-lg hover:bg-slate-700/50 hover:text-white transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (selectedProject) {
                  handleDeleteProject(selectedProject.id);
                }
              }}
              disabled={deleteProjectMutation.isPending}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg hover:from-red-700 hover:to-rose-700 transition-all duration-200 shadow-lg shadow-red-600/20 hover:shadow-red-600/30 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {deleteProjectMutation.isPending && (
                <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
              )}
              <span>
                {deleteProjectMutation.isPending ? "Deleting..." : "Delete projects"}
              </span>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectManagement;
